import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import myToast from '../Utils/MyToast'
import COLORS from '../resources/colors/COLORS'
import axios from 'axios'
import SessionManager from '../Utils/SessionManager'
import '../css/Admin_css/digital-sign-dialog.css'

// ============================================================================
// CONSTANTS
// ============================================================================

const FUNCTION_NAME = 'ManageSchoolDigitalSign.php'
const VALID_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/bmp']
const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB
// More aggressive thresholds to better remove light/grey backgrounds
const IMAGE_PROCESSING_CONFIG = {
  CHUNK_SIZE: 50,
  // how different a pixel can be from the top-left background color
  // and still be treated as background
  BG_THRESHOLD: 60,
  // any pixel with R,G,B above this is treated as white/near-white and made transparent
  WHITE_THRESHOLD: 235,
  MAX_WIDTH: 500,
  MAX_HEIGHT: 220,
  PNG_QUALITY: 0.92,
}

// ============================================================================
// HELPER FUNCTIONS (moved outside component to prevent recreation)
// ============================================================================

  const createObjectURL = (fileOrBlob) => {
  if (typeof window !== 'undefined' && window.URL?.createObjectURL) {
      return window.URL.createObjectURL(fileOrBlob)
  }
  if (typeof URL !== 'undefined' && URL.createObjectURL) {
      return URL.createObjectURL(fileOrBlob)
  }

  // Some environments (older browsers, test runners, SSR) don't support
  // URL.createObjectURL. In that case we gracefully fall back to other
  // techniques (FileReader/data URL) at call sites instead of throwing.
  return null
}

  const revokeObjectURL = (url) => {
  if (url?.startsWith('blob:')) {
    if (typeof window !== 'undefined' && window.URL?.revokeObjectURL) {
          window.URL.revokeObjectURL(url)
        } else if (typeof URL !== 'undefined' && URL.revokeObjectURL) {
          URL.revokeObjectURL(url)
        }
  }
}


// ============================================================================
// IMAGE PROCESSING FUNCTIONS
// ============================================================================

const detectContentBounds = (data, width, height, bgR, bgG, bgB) => {
  let minX = width
  let minY = height
  let maxX = 0
  let maxY = 0
  let found = false

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const idx = (y * width + x) * 4
      const r = data[idx]
      const g = data[idx + 1]
      const b = data[idx + 2]

      const bgDiff = Math.abs(r - bgR) + Math.abs(g - bgG) + Math.abs(b - bgB)
      const isNearBg = bgDiff < IMAGE_PROCESSING_CONFIG.BG_THRESHOLD
      const isNearWhite = r > IMAGE_PROCESSING_CONFIG.WHITE_THRESHOLD && 
                         g > IMAGE_PROCESSING_CONFIG.WHITE_THRESHOLD && 
                         b > IMAGE_PROCESSING_CONFIG.WHITE_THRESHOLD

      if (!isNearBg && !isNearWhite) {
        found = true
        if (x < minX) minX = x
        if (y < minY) minY = y
        if (x > maxX) maxX = x
        if (y > maxY) maxY = y
      }
    }
  }

  return found ? { minX, minY, maxX, maxY } : null
}

const makeTransparent = (imageData, threshold = IMAGE_PROCESSING_CONFIG.WHITE_THRESHOLD) => {
  const data = imageData.data
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i]
    const g = data[i + 1]
    const b = data[i + 2]
    if (r > threshold && g > threshold && b > threshold) {
      data[i + 3] = 0 // alpha 0
    }
  }
  return imageData
}

const scaleCanvas = (canvas, maxWidth, maxHeight) => {
  const { width, height } = canvas
  const scale = Math.min(1, maxWidth / width, maxHeight / height)
  
  if (scale >= 1) return canvas

  const newWidth = Math.floor(width * scale)
  const newHeight = Math.floor(height * scale)
  const scaledCanvas = document.createElement('canvas')
  scaledCanvas.width = newWidth
  scaledCanvas.height = newHeight
  const ctx = scaledCanvas.getContext('2d')
  ctx.drawImage(canvas, 0, 0, newWidth, newHeight)
  
  return scaledCanvas
}

// ============================================================================
// COMPONENT
// ============================================================================

const MdlManageDigitalSign = (props) => {
  const { show, mode, sign, actionClose } = props
  const fileInputRef = useRef(null)
  
  const isEdit = useMemo(() => mode === 'edit', [mode])

  const [authorityName, setAuthorityName] = useState('')
  const [designation, setDesignation] = useState('')
  const [remark, setRemark] = useState('')
  const [file, setFile] = useState(null)
  const [previewUrl, setPreviewUrl] = useState('')
  const [hasImage, setHasImage] = useState(false)
  const [saving, setSaving] = useState(false)
  const [processingPreview, setProcessingPreview] = useState(false)
  const [loadingPreview, setLoadingPreview] = useState(false)

  // Reset form state
  const resetForm = useCallback(() => {
    setAuthorityName('')
    setDesignation('')
    setRemark('')
    setFile(null)
    setPreviewUrl('')
    setHasImage(false)
    setLoadingPreview(false)
    setProcessingPreview(false)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [])

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      if (previewUrl?.startsWith('blob:')) {
        revokeObjectURL(previewUrl)
      }
    }
  }, [previewUrl])

  // Initialize form when dialog opens/closes or sign changes
  useEffect(() => {
    if (!show) {
      resetForm()
      return
    }

    if (isEdit && sign) {
      setAuthorityName(sign.authority_name || '')
      setDesignation(sign.authority_designation || '')
      setRemark(sign.remark || '')
      if (sign.scanned_sign) {
        const serverUrl = `${process.env.REACT_APP_SERVER_NAME}${sign.scanned_sign}`
        setPreviewUrl(serverUrl)
        setHasImage(true)
      } else {
        setPreviewUrl('')
        setHasImage(false)
      }
      setFile(null)
    } else {
      resetForm()
    }
  }, [show, isEdit, sign, resetForm])

  const handleFileChange = useCallback(
    (e) => {
      try {
        const f = e.target.files?.[0]
        if (!f) {
          setFile(null)
          setHasImage(false)
          setPreviewUrl('')
          return
        }

        if (!f.type || !VALID_IMAGE_TYPES.includes(f.type.toLowerCase())) {
          myToast.notify_warning('Please select a valid image file (JPEG, PNG, GIF, WebP, or BMP)')
          e.target.value = ''
          return
        }

        if (f.size === 0) {
          myToast.notify_warning('Selected file is empty')
          e.target.value = ''
          return
        }

        if (f.size > MAX_FILE_SIZE) {
          myToast.notify_warning('Image size should be less than 10MB')
          e.target.value = ''
          return
        }

        // Clean up any previous blob preview
        if (previewUrl?.startsWith('blob:')) {
          revokeObjectURL(previewUrl)
        }

        setFile(f)

        const url = createObjectURL(f)

        if (url) {
          // Modern browsers: use object URL for preview
          setPreviewUrl(url)
          setHasImage(true)
          setLoadingPreview(false)
          return
        }

        // Fallback: use FileReader + data URL for environments
        // without URL.createObjectURL support.
        if (typeof FileReader !== 'undefined') {
          const reader = new FileReader()
          reader.onload = (ev) => {
            const result = ev.target?.result
            if (typeof result === 'string') {
              setPreviewUrl(result)
              setHasImage(true)
            } else {
              myToast.notify_error('Unable to preview image. Please try another file.')
              setPreviewUrl('')
              setHasImage(false)
            }
            setLoadingPreview(false)
          }
          reader.onerror = () => {
            myToast.notify_error('Unable to load image. Please try another file.')
            setPreviewUrl('')
            setHasImage(false)
            setLoadingPreview(false)
          }
          reader.readAsDataURL(f)
        } else {
          // Very old / limited environments: no preview, but allow upload.
          myToast.notify_warning('Image preview is not supported in this browser, but the file will be uploaded.')
          setPreviewUrl('')
          setHasImage(false)
          setLoadingPreview(false)
        }
      } catch (err) {
        myToast.notify_error('Unable to load image. Please try another file.')
      setFile(null)
      setPreviewUrl('')
      setHasImage(false)
      setLoadingPreview(false)
      }
    },
    [previewUrl]
  )

  const handleImageError = useCallback(() => {
    if (previewUrl?.startsWith('blob:')) {
      revokeObjectURL(previewUrl)
    }
    setHasImage(false)
    setPreviewUrl('')
    setFile(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
    myToast.notify_error('Failed to load image. Please try selecting the image again.')
  }, [previewUrl])

  const handleLocalProcess = useCallback(
    async () => {
    try {
        if (!previewUrl) {
          myToast.notify_warning('No image available to process. Please select an image first.')
          return
        }

        const isLocalPreview =
          typeof previewUrl === 'string' &&
          (previewUrl.startsWith('blob:') || previewUrl.startsWith('data:'))

        // For existing server images without CORS, canvas readback will fail.
        // Only allow client-side cropping for locally selected images.
        if (!isLocalPreview) {
          myToast.notify_warning('To crop the signature, please re-select the image from your device.')
        return
      }

      setProcessingPreview(true)

      const img = new Image()
      img.crossOrigin = 'anonymous'

      const loadPromise = new Promise((resolve, reject) => {
        img.onload = () => resolve(null)
        img.onerror = (err) => {
          reject(err)
        }
      })

        // Use the current preview URL (supports both blob: URLs and data URLs,
        // including images loaded from the server in edit mode)
        img.src = previewUrl
      await loadPromise

        const { width, height } = img
      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height
      const ctx = canvas.getContext('2d')
      ctx.drawImage(img, 0, 0)

      const imgData = ctx.getImageData(0, 0, width, height)
      const data = imgData.data

      // Detect background color from top-left pixel
      const bgR = data[0]
      const bgG = data[1]
      const bgB = data[2]

        // Detect content bounds
        const bounds = detectContentBounds(data, width, height, bgR, bgG, bgB)

        if (!bounds) {
          myToast.notify_info('Could not detect clear content to crop; using full image')
          setProcessingPreview(false)
          return
        }

        const { minX, minY, maxX, maxY } = bounds
        const cropWidth = maxX - minX + 1
        const cropHeight = maxY - minY + 1

        const cropCanvas = document.createElement('canvas')
        cropCanvas.width = cropWidth
        cropCanvas.height = cropHeight
        const cropCtx = cropCanvas.getContext('2d')

        const croppedData = ctx.getImageData(minX, minY, cropWidth, cropHeight)
        const processedData = makeTransparent(croppedData)
        cropCtx.putImageData(processedData, 0, 0)

        // Scale down if too big
        const finalCanvas = scaleCanvas(
          cropCanvas,
          IMAGE_PROCESSING_CONFIG.MAX_WIDTH,
          IMAGE_PROCESSING_CONFIG.MAX_HEIGHT
        )

        finalCanvas.toBlob(
          (blob) => {
            if (blob) {
              // Update preview: try object URL, fall back to data URL if needed
              const updatePreviewFromBlob = (dataUrl) => {
              setPreviewUrl((oldUrl) => {
                  if (oldUrl?.startsWith('blob:')) {
                  revokeObjectURL(oldUrl)
                }
                  return dataUrl
                })
              }

              const objectUrl = createObjectURL(blob)
              if (objectUrl) {
                updatePreviewFromBlob(objectUrl)
              } else if (typeof FileReader !== 'undefined') {
                const reader = new FileReader()
                reader.onload = (ev) => {
                  const result = ev.target?.result
                  if (typeof result === 'string') {
                    updatePreviewFromBlob(result)
                  } else {
                    myToast.notify_error('Unable to preview processed image.')
                  }
                }
                reader.onerror = () => {
                  myToast.notify_error('Unable to preview processed image.')
                }
                reader.readAsDataURL(blob)
              } else {
                myToast.notify_warning('Image preview not supported, but the processed image will be saved.')
              }

              const processedFile = new File([blob], 'processed_sign.png', { type: 'image/png' })
              setFile(processedFile)
              setHasImage(true)
              myToast.notify_success('Preview cropped and background removed (client-side)')
            } else {
              myToast.notify_error('Failed to generate preview image')
            }
            setProcessingPreview(false)
          },
          'image/png',
          IMAGE_PROCESSING_CONFIG.PNG_QUALITY
        )
        } catch (err) {
        myToast.notify_error('Error processing preview image')
        setProcessingPreview(false)
      }
    },
    [previewUrl]
  )

  const handleClose = useCallback(() => {
    if (previewUrl?.startsWith('blob:')) {
      revokeObjectURL(previewUrl)
    }
    if (actionClose) {
      actionClose()
    }
  }, [previewUrl, actionClose])

  const handleSave = useCallback(async () => {
    try {
      if (!authorityName.trim()) {
        myToast.notify_warning('Please enter authority name')
        return
      }

      const schoolCode = SessionManager.getSchoolCode?.()
      if (!schoolCode) {
        myToast.notify_warning('School code not found')
        return
      }

      const formData = new FormData()
      formData.append('action', isEdit ? 'update' : 'add')
      formData.append('school_code', schoolCode)
      if (isEdit && sign?.id) {
        formData.append('id', sign.id)
      }
      formData.append('authority_name', authorityName.trim())
      formData.append('authority_designation', designation.trim())
      formData.append('remark', remark.trim())
      if (file) {
        formData.append('scanned_sign', file)
      }

      const URL = `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}${FUNCTION_NAME}`

      setSaving(true)

      const response = await axios.post(URL, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      if (response.data?.api_status === 'OK') {
        myToast.notify_success(response.data?.api_remark || 'Sign saved successfully')
        if (actionClose) {
          actionClose(true)
        }
      } else {
        myToast.notify_warning(response.data?.api_remark || 'Failed to save sign')
      }
    } catch (error) {
      myToast.notify_error('Error saving digital sign')
    } finally {
      setSaving(false)
    }
  }, [authorityName, designation, remark, file, isEdit, sign, actionClose])

  const primaryColor = COLORS.primary_color || '#2563eb'

  if (!show) {
    return null
  }

  return (
    <div className="digital-sign-overlay" onClick={handleClose}>
      <div onClick={(e) => e.stopPropagation()} className="digital-sign-modal">
        <div className="digital-sign-header">
          <h6 className="digital-sign-title">
            <i className="fa fa-pen-nib" style={{ color: primaryColor }} />
            {isEdit ? 'Edit Digital Sign' : 'Add New Digital Sign'}
          </h6>
          <button type="button" onClick={handleClose} className="digital-sign-close-button">
            ×
          </button>
        </div>

        <div className="digital-sign-form-container">
          <div>
            <label className="digital-sign-label">Authority Name</label>
            <input
              type="text"
              value={authorityName}
              onChange={(e) => setAuthorityName(e.target.value)}
              className="digital-sign-input"
            />
          </div>
          <div>
            <label className="digital-sign-label">Signature Image</label>
            <div className="digital-sign-image-container">
              <div className="digital-sign-preview-box">
                {loadingPreview ? (
                  <div className="digital-sign-loading-container">
                    <i className="fa fa-spinner fa-spin" style={{ fontSize: '16px', color: primaryColor }} />
                    <span className="digital-sign-loading-text">Loading...</span>
                  </div>
                ) : hasImage && previewUrl ? (
                  <img
                    src={previewUrl}
                    alt="Sign preview"
                    className="digital-sign-preview-image"
                    onError={handleImageError}
                    onLoad={() => setLoadingPreview(false)}
                  />
                ) : (
                  <span className="digital-sign-preview-placeholder">No image selected</span>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  style={{ fontSize: '12px' }}
                />
                <div className="digital-sign-button-container">
                  <button
                    type="button"
                    onClick={handleLocalProcess}
                    disabled={
                      processingPreview ||
                      !hasImage ||
                      !previewUrl ||
                      !(
                        typeof previewUrl === 'string' &&
                        (previewUrl.startsWith('blob:') || previewUrl.startsWith('data:'))
                      )
                    }
                    className="digital-sign-process-button"
                    style={{
                      backgroundColor: processingPreview ? '#9ca3af' : primaryColor,
                    }}
                  >
                    {processingPreview ? 'Processing...' : 'Crop to Content & Remove BG'}
                  </button>
                  <span className="digital-sign-process-button-text">
                    Server will also auto-crop, remove background, and save as optimized PNG.
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div>
            <label className="digital-sign-label">Designation</label>
            <input
              type="text"
              value={designation}
              onChange={(e) => setDesignation(e.target.value)}
              className="digital-sign-input"
            />
          </div>
          <div>
            <label className="digital-sign-label">Remark</label>
            <textarea
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              rows={3}
              className="digital-sign-textarea"
            />
          </div>
        </div>

        <div className="digital-sign-actions-container">
          <button type="button" onClick={handleClose} className="digital-sign-cancel-button">
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="digital-sign-save-button"
            style={{
              backgroundColor: primaryColor,
              opacity: saving ? 0.7 : 1,
            }}
            disabled={saving}
          >
            {saving ? 'Saving...' : isEdit ? 'Update' : 'Add'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default MdlManageDigitalSign


